import { Button, Text } from "@kvix/ui";
import { CircularProgress, Fade } from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import React, { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMount } from "react-use";
import styled from "styled-components";
import { PurchaseFlowContext } from "../../../../../../contexts/PurchaseFlow";
import { Platform } from "../../../../../../utils/platform";
import { PaymentContext } from "../../../../Payment/context";
import { useDefaultPaymentMethodId } from "../../../../Payment/hooks";
import { CreditCardFormDialog } from "../../../../Payment/PaymentMethods/Card/Form";
import { CardPreviewRow } from "../../../../Payment/PaymentMethods/Card/Preview";
import { ProfileSection } from "../../../partials/Section";

const Container = styled.div`
  position: relative;

  display: grid;
  grid-gap: 16px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  backdrop-filter: grayscale(80%);
`;

const CardsContainer = styled.div<{ faded: boolean }>`
  display: grid;
  grid-auto-flow: row;
  grid-gap: 8px;

  opacity: ${(props) => (props.faded ? 0.3 : 1)};
  user-select: ${(props) => (props.faded ? "none" : null)};
`;

export const AccountSectionPaymentMethod: FC = (props) => {
  const {
    savedCards: [cards, loading],
    actions,
  } = useContext(PaymentContext);
  const defaultCard = useDefaultPaymentMethodId();
  const [modalOpen, setModalOpen] = useState(false);
  const { t, ready } = useTranslation("account");
  const { context } = useContext(PurchaseFlowContext);

  const open = () => setModalOpen(true);
  const close = () => setModalOpen(false);
  useMount(actions.fetchSavedCards);

  if (cards.length === 0 && loading) {
    return null;
  }

  const hasOneOrMoreCards = cards.length > 0;
  const showNoActiveCard =
    !defaultCard || !cards.find((card) => card.id === defaultCard);

  if (!ready) {
    return null;
  }

  return context?.platform !== Platform.IOS ? (
    <ProfileSection
      title={<Text variant="h5">{t("account.paymentMethod.title")}</Text>}
      action={
        <>
          <Button variant="text" size="small" onClick={open}>
            {t("account.paymentMethod.addCard")}
          </Button>
        </>
      }
    >
      <Container>
        {!hasOneOrMoreCards && (
          <Alert variant="filled" severity="error">
            <AlertTitle>
              {t("account.paymentMethod.noConnectedCardError")}
            </AlertTitle>
          </Alert>
        )}

        {hasOneOrMoreCards && showNoActiveCard && (
          <Alert variant="filled" severity="error">
            {t("account.paymentMethod.noActiveCardError")}
          </Alert>
        )}

        {hasOneOrMoreCards && (
          <CardsContainer faded={loading}>
            {cards.map((card) => (
              <CardPreviewRow paymentMethod={card} key={card.id} />
            ))}

            <Fade mountOnEnter unmountOnExit in={loading}>
              <LoadingOverlay>
                <CircularProgress />
              </LoadingOverlay>
            </Fade>
          </CardsContainer>
        )}
        <CreditCardFormDialog open={modalOpen} onClose={close} />
      </Container>
    </ProfileSection>
  ) : null;
};
